<template>
  <div v-if="$vuetifyDisplay.mdAndDown" id="subheader">
    <!--
      Vuetify 4's v-overlay teleports its content to a global
      .v-overlay-container on document.body by default, so it's no longer a
      DOM descendant of #subheader - the `#subheader .v-list` positioning
      rule below (fixed, full-width, pinned under the header) then silently
      never matches, and the menu falls back to Vuetify's own default
      centered overlay positioning. `attach` disables the teleport so this
      stays in place where that CSS can reach it, matching master's
      full-width dropdown.
    -->
    <v-overlay :value="show" attach>
      <v-list
        dense
        class="pa-0"
        light
        v-click-outside="{ handler: onClickOutside }"
      >
        <v-list-item
          v-if="$vuetifyDisplay.smAndDown && userGuideEnabled"
          :to="{ name: 'user-guide' }"
        >
          <v-list-item-content>
            <v-list-item-title>
              <div class="p-list-item">User Guide</div>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-divider class="mx-4"></v-divider>

        <v-list-item :to="{ name: 'dashboard' }">
          <v-list-item-content>
            <v-list-item-title>
              <div class="p-list-item">Dashboard</div>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item :to="{ name: 'purchase-orders' }">
          <v-list-item-content>
            <v-list-item-title>
              <div class="p-list-item">
                Purchase Orders
                <v-badge
                  v-if="liveOrdersTotal"
                  overlap
                  class="ml-1"
                  color="orange"
                  :content="liveOrdersTotal"
                  :class="{ 'p-badge-updated': purchaseOrdersBadgeUpdated }"
                >
                </v-badge>
              </div>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item :to="{ name: 'order-queries' }">
          <v-list-item-content>
            <v-list-item-title>
              <div class="p-list-item">
                Order Queries
                <v-badge
                  v-if="outstandingOrderQueries"
                  overlap
                  class="ml-1"
                  color="orange"
                  :content="outstandingOrderQueries"
                  :class="{ 'p-badge-updated': orderQueriesBadgeUpdated }"
                >
                </v-badge>
              </div>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item
          v-if="creditReturnEnabled && featureCreditReturnsEnabled"
          :to="{ name: 'credit-returns' }"
        >
          <v-list-item-content>
            <v-list-item-title>
              <div class="p-list-item">
                Credit Returns
                <v-badge
                  v-if="outstandingCreditReturns"
                  overlap
                  class="ml-1"
                  color="orange"
                  :content="outstandingCreditReturns"
                  :class="{ 'p-badge-updated': creditReturnsBadgeUpdated }"
                >
                </v-badge>
              </div>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item :to="{ name: 'data-maintenance' }">
          <v-list-item-content>
            <v-list-item-title>
              <div class="p-list-item">
                Data Setup & Maintenance
                <v-badge
                  v-if="totalRequiringAmendmentsCount"
                  overlap
                  class="ml-1"
                  color="orange"
                  :content="totalRequiringAmendmentsCount"
                  :class="{ 'p-badge-updated': dataMaintenanceBadgeUpdated }"
                >
                </v-badge>
              </div>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-list-item :to="{ name: 'reports' }">
          <v-list-item-content>
            <v-list-item-title>
              <div class="p-list-item">Reports</div>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <v-divider class="mx-4"></v-divider>

        <v-list-item @click="logout">
          <v-list-item-content>
            <v-list-item-title>
              <div class="p-list-item">Sign out</div>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-overlay>
  </div>

  <div v-else id="subheader">
    <v-tabs fixed-tabs background-color="primary" optional>
      <v-tab :to="{ name: 'dashboard' }"> Dashboard </v-tab>

      <v-tab :to="{ name: 'purchase-orders' }">
        Purchase Orders
        <v-badge
          v-if="liveOrdersTotal"
          overlap
          class="ml-1"
          color="orange"
          :content="liveOrdersTotal"
          :class="{ 'p-badge-updated': purchaseOrdersBadgeUpdated }"
        >
        </v-badge>
      </v-tab>

      <v-tab :to="{ name: 'order-queries' }">
        Order Queries
        <v-badge
          v-if="outstandingOrderQueries"
          overlap
          class="ml-1"
          color="orange"
          :content="outstandingOrderQueries"
          :class="{ 'p-badge-updated': orderQueriesBadgeUpdated }"
        >
        </v-badge>
      </v-tab>

      <v-tab
        v-if="creditReturnEnabled && featureCreditReturnsEnabled"
        :to="{ name: 'credit-returns' }"
      >
        Credit Returns
        <v-badge
          v-if="outstandingCreditReturns"
          overlap
          class="ml-1"
          color="orange"
          :content="outstandingCreditReturns"
          :class="{ 'p-badge-updated': creditReturnsBadgeUpdated }"
        >
        </v-badge>
      </v-tab>

      <v-tab :to="{ name: 'data-maintenance' }">
        Data Setup & Maintenance
        <v-badge
          v-if="totalRequiringAmendmentsCount"
          overlap
          class="ml-1"
          color="orange"
          :content="totalRequiringAmendmentsCount"
          :class="{ 'p-badge-updated': dataMaintenanceBadgeUpdated }"
        >
        </v-badge>
      </v-tab>

      <v-tab :to="{ name: 'reports' }"> Reports </v-tab>
    </v-tabs>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

export default {
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    purchaseOrdersBadgeUpdated: false,
    orderQueriesBadgeUpdated: false,
    creditReturnsBadgeUpdated: false,
    dataMaintenanceBadgeUpdated: false
  }),

  computed: {
    ...mapGetters('creditReturns', ['outstandingCreditReturns']),
    ...mapGetters('orderQueries', ['outstandingOrderQueries']),
    ...mapGetters('purchaseOrders', ['liveOrdersTotal']),
    ...mapGetters('dataMaintenance', ['totalRequiringAmendmentsCount']),
    ...mapGetters('profile', ['creditReturnEnabled']),
    ...mapGetters('tenant', [
      'featureCreditReturnsEnabled',
      'userGuideEnabled'
    ]),

    ...mapState('profile', {
      supplierSuburb: (state) => `${state.info?.billingAddress?.suburb}`,
      supplierPostCode: (state) => `${state.info?.billingAddress?.postCode}`
    })
  },

  methods: {
    ...mapActions('auth', ['logout']),

    onClickOutside() {
      this.$emit('close')
    }
  },

  watch: {
    liveOrdersTotal() {
      this.purchaseOrdersBadgeUpdated = true
      setTimeout(() => {
        this.purchaseOrdersBadgeUpdated = false
      }, 1000)
    },

    outstandingOrderQueries() {
      this.orderQueriesBadgeUpdated = true
      setTimeout(() => {
        this.orderQueriesBadgeUpdated = false
      }, 1000)
    },

    outstandingCreditReturns() {
      this.creditReturnsBadgeUpdated = true
      setTimeout(() => {
        this.creditReturnsBadgeUpdated = false
      }, 1000)
    },

    totalRequiringAmendmentsCount() {
      this.dataMaintenanceBadgeUpdated = true
      setTimeout(() => {
        this.dataMaintenanceBadgeUpdated = false
      }, 1000)
    }
  }
}
</script>

<style lang="scss">
#subheader {
  // v-overlay__content has `contain: layout`, which makes IT the containing
  // block for any `position: fixed` descendant (per spec, same effect as
  // `transform`) - so fixing .v-list itself resolves left/right against
  // that content wrapper's own (content-sized, ~0px) box instead of the
  // viewport, collapsing it to zero width. Fixing the wrapper instead
  // resolves correctly against the viewport, and .v-list just fills it.
  .v-overlay__content {
    position: fixed !important;
    top: 55px !important;
    left: 0;
    right: 0;
    width: auto;
  }
  .v-list {
    background-color: white;
  }
  .p-badge-updated {
    .v-badge__badge {
      animation: blink 0.5s;
    }
  }
  .p-delivery-item {
    background-color: #304788;
    border-radius: 0;
  }
  .p-list-item {
    height: 30px;
    display: flex;
    align-items: center;
  }
  .mdi {
    color: white;
    font-size: 28px;
  }
}

@media only screen and (min-width: 960px) {
  #subheader {
    .mdi {
      color: white;
      font-size: 32px;
    }
    .p-delivery-text {
      font-size: 14px;
    }
    .v-tab {
      font-size: 16px;
      font-weight: 500;
      text-transform: capitalize;
      color: rgba(255, 255, 255, 0.75);

      &--active,
      &--selected,
      &.v-btn--active {
        color: white;
      }
      &.location {
        opacity: 1;
        font-weight: normal;
        justify-content: left;
        max-width: 250px;
      }
    }
  }
}
</style>
